import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  SelectChangeEvent,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { challengeService, CreateChallengeData } from '../../services/challengeService';

const ChallengeForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateChallengeData>({
    title: '',
    description: '',
    difficulty: 'Easy',
    points: 0,
    validation_script: '',
    docker_image: '',
    setup_script: '',
    max_attempts: 3,
    time_limit_minutes: 30,
    tags: [],
    prerequisites: [],
    hints: [],
    is_active: true,
  });
  const [newTag, setNewTag] = useState('');
  const [newPrerequisite, setNewPrerequisite] = useState('');
  const [newHint, setNewHint] = useState('');

  useEffect(() => {
    if (isEditMode && id) {
      fetchChallenge(parseInt(id));
    }
  }, [isEditMode, id]);

  const fetchChallenge = async (challengeId: number) => {
    try {
      const response = await challengeService.listChallenges({ skip: 0, limit: 1, search: challengeId.toString() });
      if (response.data.length > 0) {
        const challenge = response.data[0];
        setFormData({
          title: challenge.title,
          description: challenge.description,
          difficulty: challenge.difficulty,
          points: challenge.points,
          validation_script: challenge.validation_script,
          docker_image: challenge.docker_image,
          setup_script: challenge.setup_script,
          max_attempts: challenge.max_attempts,
          time_limit_minutes: challenge.time_limit_minutes,
          tags: challenge.tags,
          prerequisites: challenge.prerequisites,
          hints: challenge.hints,
          is_active: challenge.is_active,
        });
      }
    } catch (error) {
      console.error('Error fetching challenge:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEditMode && id) {
        await challengeService.updateChallenge(parseInt(id), formData);
      } else {
        await challengeService.createChallenge(formData);
      }
      navigate('/admin/challenges');
    } catch (error) {
      console.error('Error saving challenge:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;
    if (name) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleAddTag = () => {
    if (newTag.trim()) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleAddPrerequisite = () => {
    if (newPrerequisite.trim()) {
      setFormData(prev => ({
        ...prev,
        prerequisites: [...(prev.prerequisites || []), parseInt(newPrerequisite.trim())]
      }));
      setNewPrerequisite('');
    }
  };

  const handleAddHint = () => {
    if (newHint.trim()) {
      setFormData(prev => ({
        ...prev,
        hints: [...(prev.hints || []), newHint.trim()]
      }));
      setNewHint('');
    }
  };

  const handleArrayItemDelete = (field: keyof CreateChallengeData, index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] as any[]).filter((_, i) => i !== index)
    }));
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Edit Challenge' : 'Create Challenge'}
        </Typography>
        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    multiline
                    rows={4}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Difficulty</InputLabel>
                    <Select
                      name="difficulty"
                      value={formData.difficulty}
                      label="Difficulty"
                      onChange={handleInputChange}
                    >
                      <MenuItem value="Easy">Easy</MenuItem>
                      <MenuItem value="Medium">Medium</MenuItem>
                      <MenuItem value="Hard">Hard</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Points"
                    name="points"
                    type="number"
                    value={formData.points}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Validation Script"
                    name="validation_script"
                    value={formData.validation_script}
                    onChange={handleInputChange}
                    multiline
                    rows={4}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Docker Image"
                    name="docker_image"
                    value={formData.docker_image}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Setup Script"
                    name="setup_script"
                    value={formData.setup_script}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Max Attempts"
                    name="max_attempts"
                    type="number"
                    value={formData.max_attempts}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Time Limit (minutes)"
                    name="time_limit_minutes"
                    type="number"
                    value={formData.time_limit_minutes}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6">Tags</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        size="small"
                        value={newTag}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTag(e.target.value)}
                        placeholder="Add tag"
                      />
                      <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddTag}
                        disabled={!newTag.trim()}
                      >
                        Add
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.tags?.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          onDelete={() => handleArrayItemDelete('tags', index)}
                        />
                      ))}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6">Prerequisites</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        size="small"
                        value={newPrerequisite}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPrerequisite(e.target.value)}
                        placeholder="Add prerequisite ID"
                        type="number"
                      />
                      <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddPrerequisite}
                        disabled={!newPrerequisite.trim()}
                      >
                        Add
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.prerequisites?.map((prerequisite, index) => (
                        <Chip
                          key={index}
                          label={prerequisite}
                          onDelete={() => handleArrayItemDelete('prerequisites', index)}
                        />
                      ))}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6">Hints</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        size="small"
                        value={newHint}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewHint(e.target.value)}
                        placeholder="Add hint"
                      />
                      <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddHint}
                        disabled={!newHint.trim()}
                      >
                        Add
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.hints?.map((hint, index) => (
                        <Chip
                          key={index}
                          label={hint}
                          onDelete={() => handleArrayItemDelete('hints', index)}
                        />
                      ))}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/admin/challenges')}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : isEditMode ? 'Update Challenge' : 'Create Challenge'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ChallengeForm; 